/**
 * IssueCard - Individual Issue Display Component
 * 
 * Why this matters: Presents each identified plant issue with severity,
 * confidence, description, and likely causes in an easy-to-understand format.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, AlertTriangle, AlertCircle, Info, Leaf, Apple, TreeDeciduous } from 'lucide-react';
import { DiagnosisIssue } from './CitrusDoctor';

interface IssueCardProps {
  issue: DiagnosisIssue;
  index: number;
  imageUrl?: string;
}

const IssueCard: React.FC<IssueCardProps> = ({ issue, index, imageUrl }) => {
  const [isExpanded, setIsExpanded] = useState(index === 0); // First issue expanded by default

  /**
   * Get image position based on issue keywords
   * Why this matters: Creates visual focus on the affected area
   */
  const getImagePosition = () => {
    const name = issue.name.toLowerCase();
    const areas = issue.affectedAreas?.map(a => a.toLowerCase()) || [];
    
    if (name.includes('leaf') || areas.includes('leaves')) {
      return { x: '30%', y: '20%' };
    } else if (name.includes('root') || areas.includes('roots')) {
      return { x: '50%', y: '80%' };
    } else if (name.includes('trunk') || name.includes('bark') || areas.includes('trunk')) {
      return { x: '50%', y: '60%' };
    } else if (name.includes('fruit') || areas.includes('fruit')) {
      return { x: '60%', y: '30%' };
    }
    return { x: '50%', y: '40%' };
  };

  /**
   * Get severity icon and color
   */
  const getSeverityInfo = () => {
    switch (issue.severity) {
      case 'high':
        return { icon: AlertTriangle, color: 'severity-high', label: 'High Priority' };
      case 'medium':
        return { icon: AlertCircle, color: 'severity-medium', label: 'Medium Priority' };
      case 'low':
        return { icon: Info, color: 'severity-low', label: 'Low Priority' };
      default:
        return { icon: Info, color: 'severity-medium', label: 'Priority' };
    }
  };

  /**
   * Get issue type icon
   */
  const getTypeIcon = () => {
    switch (issue.type) {
      case 'deficiency':
        return Leaf;
      case 'pest':
        return AlertTriangle;
      case 'disease':
        return AlertCircle;
      case 'environmental':
        return TreeDeciduous;
      case 'care':
        return Apple;
      default:
        return Info;
    }
  };

  /**
   * Get affected area display
   */
  const getAffectedAreasDisplay = () => {
    if (!issue.affectedAreas || issue.affectedAreas.length === 0) return null;
    return issue.affectedAreas.map(area => 
      area.charAt(0).toUpperCase() + area.slice(1)
    ).join(', ');
  };

  const severityInfo = getSeverityInfo();
  const SeverityIcon = severityInfo.icon;
  const TypeIcon = getTypeIcon();
  const affectedAreas = getAffectedAreasDisplay();

  return (
    <div className={`issue-card ${severityInfo.color} ${isExpanded ? 'expanded' : ''}`}>
      {/* Two-column layout: Left (header + image) | Right (details) */}
      <div className="issue-card-layout">
        {/* LEFT COLUMN: Header + Image */}
        <div className="issue-left-column">
          {/* Header - Clickable to expand/collapse */}
          <button 
            className="issue-card-header"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
          >
            <div className="issue-header-badges">
              <div className={`issue-severity-badge ${severityInfo.color}`}>
                <SeverityIcon size={14} />
                <span>{issue.severity}</span>
              </div>
              <div className="issue-type-badge">
                <TypeIcon size={14} />
                <span>{issue.type}</span>
              </div>
            </div>
            
            <div className="issue-header-title-row">
              <h4 className="issue-name">{issue.name}</h4>
              <div className="issue-header-right">
                <span className="issue-confidence">{issue.confidence}% confident</span>
                {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </div>
            </div>
          </button>

          {/* Image - Below header in left column */}
          {isExpanded && imageUrl && (
            <div className="issue-image-section">
              <div 
                className="issue-cropped-image"
                style={{
                  backgroundImage: `url(${imageUrl})`,
                  backgroundPosition: `${getImagePosition().x} ${getImagePosition().y}`,
                  backgroundSize: '200%'
                }}
              >
                <div className="issue-image-focus-ring" />
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Details (only when expanded) */}
        {isExpanded && (
          <div className="issue-details-section">
            {/* Description */}
            <div className="issue-section">
              <h5 className="issue-section-title">What We Found</h5>
              <p className="issue-description">{issue.description}</p>
            </div>

            {/* Affected Areas */}
            {affectedAreas && (
              <div className="issue-section">
                <h5 className="issue-section-title">Affected Areas</h5>
                <div className="issue-affected-areas">
                  {issue.affectedAreas.map((area, i) => (
                    <span key={i} className="affected-area-tag">
                      {area.charAt(0).toUpperCase() + area.slice(1)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Likely Causes */}
            {issue.likelyCauses && issue.likelyCauses.length > 0 && (
              <div className="issue-section">
                <h5 className="issue-section-title">Likely Causes</h5>
                <ul className="issue-causes-list">
                  {issue.likelyCauses.map((cause, i) => (
                    <li key={i}>{cause}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IssueCard;
